import { z } from 'zod';
import { UuidString } from '../../primitives.js';
import { TASK_BULK_MAX_IDS } from './bulk.js';
import { FilterMatch, FilterRule, type systemFilterFields } from './filter-rule.js';

/**
 * chat-interactive-actions B1 (SOLUTION §2.4) — общая модель фильтра задач.
 *
 * Зеркало `SearchQuery` ручки `/task-module/tasks/search` (фасеты + rule-DSL) без
 * search-only полей. До пака тип жил на фронте (`useTasksSearch.ts:TaskSearchFilter`)
 * и ни одна серверная сторона не могла его ни принять, ни отдать: инструмент чата
 * `apply_task_filter` обязан вернуть ровно то, что стор фронта умеет применить (I5:
 * фильтр от агента = фильтр от руки, одна модель).
 *
 * НЕ входит намеренно (search-only, к «какие задачи» отношения не имеет):
 *   - `cursor`, `limit`, `sort_by`, `group_by` — пагинация/выдача;
 *   - `include_archived` — тумблер вида (`showArchived` в workTasksViewStore), не
 *     условие отбора.
 *
 * Значения фасетов остаются строками (не enum): наборы групп мигрируются на чтении
 * (`migrateStateGroups`), а приоритеты/типы валидируются сервером — так было у
 * фронтового типа, и сужать здесь значило бы ронять старые saved-view на parse.
 */
export const TaskFilterShared = z
  .object({
    /**
     * Отбор по ЯВНОМУ списку задач (заход 2, R1 акта приёмки 02.09.2026).
     *
     * Появился ради шага проверки состава (I3, [Л9]): «Открыть все N» обязано
     * показать РОВНО тот набор, который исполнится по кнопке. Первый заход открывал
     * `filter_used` — то есть живую переоценку правила: за час состав мог измениться,
     * а у черновика из ручного выделения правила нет вовсе, и в стор уходил пустой
     * фильтр («все задачи»), заодно снимая порог В-4 без всякой проверки.
     *
     * Условие AND-ится с остальными и с видимостью: id чужой компании или закрытого
     * проекта выпадают из выдачи молча — их существование не раскрывается
     * (инвариант 3 rbac). Потолок — тот же, что у пачки (В-2: 500).
     */
    task_ids: z.array(UuidString).max(TASK_BULK_MAX_IDS).optional(),
    /**
     * Происхождение состава: черновик, из которого взят `task_ids`.
     *
     * Не второе описание отбора, а ТРАНСПОРТ: пятьсот uuid в query-строке — это
     * ~18 КБ, и запрос отбивается ещё nginx-ом (`large_client_header_buffers` по
     * умолчанию 8 КБ), не доходя до приложения. Когда список не помещается в URL,
     * фронт шлёт вместо него id черновика, а сервер читает ЗАМОРОЖЕННЫЙ состав из
     * своей же таблицы — под владельцем черновика. Список в `task_ids` при этом
     * остаётся: проектная поверхность фильтрует задачи в памяти и знать про
     * черновики не обязана.
     */
    task_ids_draft_id: UuidString.optional(),
    q: z.string().max(200).optional(),
    project_ids: z.array(UuidString).optional(),
    state_groups: z.array(z.string()).optional(),
    priorities: z.array(z.string()).optional(),
    subject_types: z.array(z.string()).optional(),
    assignee_user_ids: z.array(UuidString).optional(),
    author_user_ids: z.array(UuidString).optional(),
    label_ids: z.array(UuidString).optional(),
    no_assignee: z.boolean().optional(),
    due_from: z.string().optional(),
    due_to: z.string().optional(),
    overdue_only: z.boolean().optional(),
    has_dates: z.boolean().optional(),
    custom_field_key: z.string().max(40).optional(),
    custom_field_value: z.string().max(200).optional(),
    /** task-quick-capture B4 — пресет «Мой день». */
    view: z.enum(['my_day']).optional(),
    match: FilterMatch.optional(),
    rules: z.array(FilterRule).max(50).optional(),
  })
  // zod-strict-ok: dual-use — снимок фильтра в ui_action/ChatTaskScope и стор фронта
  .strict();
export type TaskFilterShared = z.infer<typeof TaskFilterShared>;

/**
 * Матрица покрытия project-surface (SOLUTION §7, строка «Project-surface»).
 *
 * На глобальной странице `/work-tasks` фильтр уходит на сервер и умеет все поля
 * `systemFilterFields` + `cf:*`. На проектной поверхности `/work-tasks/$projectId`
 * задачи уже в памяти и правила оценивает КЛИЕНТСКИЙ `applyTaskFilter.evalRule`,
 * который покрывает лишь часть полей, а неизвестное поле пропускает (`return true`).
 *
 * Константа — единственный источник правды о том, ЧТО клиент умеет: инструмент
 * `apply_task_filter` (B7) по ней ставит warning `project_surface_unsupported_field`,
 * а тест-сторож фронта (F13) сверяет её с фактическими ветками `evalRule` в обе
 * стороны. Живёт в shared, а не во фронте, потому что читать её обязан и бэкенд.
 */
export const PROJECT_SURFACE_SUPPORTED_RULE_FIELDS = [
  'status',
  'priority',
  'project',
  'entity',
  'assignee',
  'due_date',
  'start_date',
  'title',
] as const satisfies readonly (keyof typeof systemFilterFields)[];
export type ProjectSurfaceSupportedRuleField =
  (typeof PROJECT_SURFACE_SUPPORTED_RULE_FIELDS)[number];

const projectSurfaceSet: ReadonlySet<string> = new Set(PROJECT_SURFACE_SUPPORTED_RULE_FIELDS);

/** Поддержано ли поле правила клиентским `applyTaskFilter` проектной поверхности. */
export function isProjectSurfaceSupportedRuleField(field: string): boolean {
  return projectSurfaceSet.has(field);
}

/**
 * Поля правил фильтра, которые проектная поверхность НЕ применит (для warning
 * инструмента). `cf:*` не поддержаны целиком — клиент не читает custom-поля.
 */
export function unsupportedProjectSurfaceRuleFields(filter: TaskFilterShared): string[] {
  const out = new Set<string>();
  for (const rule of filter.rules ?? []) {
    if (!projectSurfaceSet.has(rule.field)) out.add(rule.field);
  }
  return [...out];
}
